import { useEffect, useState } from 'react'

// Breakpoint values matching Tailwind defaults
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const

export type Breakpoint = keyof typeof breakpoints

// Hook to detect current breakpoint
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('2xl')

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      if (width < breakpoints.sm) {
        setBreakpoint('sm')
      } else if (width < breakpoints.md) {
        setBreakpoint('sm')
      } else if (width < breakpoints.lg) {
        setBreakpoint('md')
      } else if (width < breakpoints.xl) {
        setBreakpoint('lg')
      } else if (width < breakpoints['2xl']) {
        setBreakpoint('xl')
      } else {
        setBreakpoint('2xl')
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return breakpoint
}

// Hook to check if screen is mobile
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoints.lg)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return isMobile
}

// Hook to check if screen is tablet
export function useIsTablet() {
  const [isTablet, setIsTablet] = useState(false)

  useEffect(() => {
    const checkTablet = () => {
      const width = window.innerWidth
      setIsTablet(width >= breakpoints.md && width < breakpoints.lg)
    }

    checkTablet()
    window.addEventListener('resize', checkTablet)
    return () => window.removeEventListener('resize', checkTablet)
  }, [])

  return isTablet
}

// Responsive container component
interface ResponsiveContainerProps {
  children: React.ReactNode
  className?: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
}

const maxWidthClasses = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
}

export function ResponsiveContainer({
  children,
  className = '',
  maxWidth = '2xl',
}: ResponsiveContainerProps) {
  return (
    <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${maxWidthClasses[maxWidth]} ${className}`}>
      {children}
    </div>
  )
}

// Responsive grid that adjusts columns based on screen size
interface ResponsiveGridProps {
  children: React.ReactNode
  className?: string
  minItemWidth?: number // Minimum width in pixels for each item
}

export function ResponsiveGrid({
  children,
  className = '',
  minItemWidth = 300,
}: ResponsiveGridProps) {
  return (
    <div
      className={`grid gap-4 sm:gap-6 ${className}`}
      style={{
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minItemWidth}px), 1fr))`,
      }}
    >
      {children}
    </div>
  )
}

// Show/hide components based on breakpoint
interface ShowAtBreakpointProps {
  children: React.ReactNode
  breakpoint: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  show?: 'above' | 'below' | 'only'
}

export function ShowAtBreakpoint({
  children,
  breakpoint,
  show = 'above',
}: ShowAtBreakpointProps) {
  const currentBreakpoint = useBreakpoint()
  const breakpointIndex = Object.keys(breakpoints).indexOf(breakpoint)
  const currentIndex = Object.keys(breakpoints).indexOf(currentBreakpoint)

  let shouldShow = false
  if (show === 'above') {
    shouldShow = currentIndex >= breakpointIndex
  } else if (show === 'below') {
    shouldShow = currentIndex < breakpointIndex
  } else if (show === 'only') {
    shouldShow = currentIndex === breakpointIndex
  }

  return shouldShow ? <>{children}</> : null
}